import { getUsers, createUser, updateUser, deleteUser } from '../models/userModel.js';
import Swal from 'sweetalert2';
import pb from '../lib/pocketbase';
import { toasts } from "svelte-toasts";

// Obtener lista de usuarios paginados
export async function fetchUsers(page, perPage) {
  try {
    const response = await getUsers(page, perPage);
    
    // Ensure role data is properly expanded for display while preserving the ID
    const usersWithExpandedRole = response.items.map(user => {
      if (user.expand && user.expand.role) {
        return {
          ...user,
          role: user.expand.role,
          roleId: user.role // Preserve the original ID for form operations
        };
      }
      return user;
    });
    
    return {
      users: usersWithExpandedRole,
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("❌ Error fetching users:", error);
    Swal.fire('Error', 'No se pudieron cargar los usuarios', 'error');
    return null;
  }
}

// Guardar o actualizar un usuario
export async function saveUser(user, currentUserId) {
  try {
    // Handle verification separately
    const { verified, ...userData } = user;
    
    if (currentUserId) {
      // Update user data - remove password fields for updates
      const { password, passwordConfirm, ...updateData } = userData;
      await updateUser(currentUserId, updateData);
      
      // If verification is requested, send verification email
      if (verified) {
        await verifyUser(currentUserId);
      }
      
      toasts.add({
        title: 'Éxito',
        description: 'Usuario actualizado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true,
      });
    } else {
      // For new users, ensure password and passwordConfirm are included
      const userDataWithPassword = {
        ...userData,
        password: userData.password || '',
        passwordConfirm: userData.passwordConfirm || userData.password || ''
      };
      
      const createdUser = await createUser(userDataWithPassword);
      
      // If verification is requested for new user, send verification email
      if (verified && createdUser && createdUser.id) {
        await verifyUser(createdUser.id);
      }
      
      toasts.add({
        title: 'Éxito',
        description: 'Usuario creado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true,
      });
    }
  } catch (error) {
    console.error("Error saving user:", error);
    
    // Handle specific validation errors
    let errorMessage = 'Ocurrió un error al guardar el usuario';
    
    if (error.data) {
      const validationErrors = [];
      
      if (error.data.password) {
        validationErrors.push(`Contraseña: ${error.data.password.message}`);
      }
      if (error.data.passwordConfirm) {
        validationErrors.push(`Confirmar Contraseña: ${error.data.passwordConfirm.message}`);
      }
      if (error.data.email) {
        validationErrors.push(`Email: ${error.data.email.message}`);
      }
      if (error.data.name) {
        validationErrors.push(`Nombre: ${error.data.name.message}`);
      }
      
      if (validationErrors.length > 0) {
        errorMessage = validationErrors.join('\n');
      }
    }
    
    toasts.add({
      title: 'Error',
      description: errorMessage,
      duration: 5000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
  }
}

// Eliminar un usuario
export async function removeUser(id) {
  try {
    await deleteUser(id);
    toasts.add({
      title: 'Éxito',
      description: 'Usuario eliminado exitosamente.',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
  } catch (error) {
    console.error("Error deleting user:", error);
    Swal.fire('Error', 'No se pudo eliminar el usuario', 'error');
  }
}

// Suscripción en tiempo real a cambios en la colección 'users'
export function subscribeToUsers(handleSearch) {
  if (typeof handleSearch !== 'function') {
    console.warn('handleSearch is not a function, real-time updates will not work');
    return;
  }
  
  pb.collection('users').subscribe('*', async (e) => {
    await handleSearch(); // Actualiza la lista manteniendo la página actual
  });
}

// Desuscripción de los eventos en tiempo real
export function unsubscribeFromUsers() {
  pb.collection('users').unsubscribe('*');
}

// Buscar usuarios por nombre, email, username o rol con paginación
export async function searchUsers(query, page = 1, perPage = 10) {
  try {
    // If query is empty, return empty results
    if (!query || query.trim() === '') {
      return {
        users: [],
        totalRecords: 0,
        currentPage: page,
      };
    }
    
    // Escape special characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    const response = await pb.collection('users').getList(page, perPage, {
      filter: `name ~ "${escapedQuery}" || email ~ "${escapedQuery}"`,
      expand: ['role'], // Expande la relación con roles
      $autoCancel: false // Disable auto-cancellation for search requests
    });
    
    // Ensure role data is properly expanded for display while preserving the ID
    const usersWithExpandedRole = response.items.map(user => {
      if (user.expand && user.expand.role) {
        return {
          ...user,
          role: user.expand.role,
          roleId: user.role // Preserve the original ID for form operations
        };
      }
      return user;
    });

    return {
      users: usersWithExpandedRole,
      totalRecords: response.totalItems,
      currentPage: response.currentPage,
    };
  } catch (error) {
    console.error('Error searching users:', error);
    return {
      users: [],
      totalRecords: 0,
      currentPage: page,
    };
  }
}

// Cambiar contraseña de usuario
export async function changePassword(userId, newPassword, oldPassword) {
  try {
    await pb.collection('users').update(userId, {
      oldPassword: oldPassword,
      password: newPassword,
      passwordConfirm: newPassword
    });
    
    toasts.add({
      title: 'Éxito',
      description: 'Contraseña actualizada exitosamente',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });
    return true;
  } catch (error) {
    console.error("Error changing password:", error);
    toasts.add({
      title: 'Error',
      description: 'No se pudo cambiar la contraseña',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
    return false;
  }
}

// Verificar un usuario
export async function verifyUser(userId) {
  try {
    // Check if userId is valid
    if (!userId) {
      console.error("No user ID provided for verification");
      return false;
    }
    
    // First get the user's email
    const user = await pb.collection('users').getOne(userId);
    
    // Request verification with the user's email
    await pb.collection('users').requestVerification(user.email);
    
    toasts.add({
      title: 'Éxito',
      description: 'Se ha enviado un correo de verificación al usuario',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });
    return true;
  } catch (error) {
    console.error("Error verifying user:", error);
    toasts.add({
      title: 'Error',
      description: 'No se pudo enviar el correo de verificación',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
    return false;
  }
}